import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import PhotoSwipe from "photoswipe";
import "photoswipe/style.css";
import { cn } from "@/lib/utils";
import { cld } from "@/lib/data";
import { Masonry } from "@/components/Masonry";
import { Play } from "lucide-react";

/**
 * MediaGallery — universal image gallery used across the site's editorial
 * surfaces (homepage selected work, /business, /life-legacy, /atelier,
 * /brand-business, and the per-category masonry views inside /portfolio).
 *
 * Behavior:
 *   - 2-column CSS masonry on mobile (≤768 px), caller-driven column count
 *     on desktop (default 3).
 *   - Each thumb uses Cloudinary's `e_blur` LQIP as a placeholder, swapping
 *     to the sharp image on `onLoad`. Mirrors a Medium / Unsplash "blur-up"
 *     pattern, but generated by Cloudinary so no client-side encoding.
 *   - `loading="lazy" decoding="async"` so off-screen rows never block the
 *     initial paint. Preserves natural aspect-ratio via CSS `aspect-ratio`
 *     so layouts don't shift when the sharp image arrives.
 *   - Tapping any thumb opens a full-screen PhotoSwipe v5 lightbox with
 *     swipe, pinch-zoom, double-tap zoom, captions, keyboard nav, and
 *     swipe-down-to-close. Bundled, not loaded from CDN.
 *   - "Load More" pagination shows `initialCount` items (default 8) and
 *     reveals `incrementCount` more (default 8) per tap. Both customizable.
 *
 * Round 2 lightbox refinements (per user spec):
 *   - Photos fill ~85–90% of the viewport via tight `paddingFn` overrides.
 *     PhotoSwipe's default padding leaves too much dark space; we reserve
 *     just enough at the bottom for the "Request a shoot like this" CTA.
 *   - Semi-transparent backdrop (`bgOpacity: 0.5`) + CSS `backdrop-filter:
 *     blur(10px)` so the page behind reads as out-of-focus rather than
 *     completely blacked out. Feels like an overlay, not a modal.
 *   - "REQUEST A SHOOT LIKE THIS" CTA registered as a PhotoSwipe UI
 *     element. Reads the current slide's `inquiryType` (or the gallery's
 *     `defaultInquiryType` prop) and navigates to
 *     `/inquire?type=<encoded>`. Closes the lightbox first so the inquire
 *     route mounts cleanly.
 *
 * Lightbox aspect-ratio handling (carry-over from Round 1):
 *   The masonry thumb ratio is for grid layout only — it does NOT match
 *   the source image's natural aspect ratio. To avoid dark bands or
 *   awkward cropping in the lightbox we:
 *     1. Capture natural width/height from each thumb's `onLoad` event
 *        and lift the value to a parent ref keyed by index.
 *     2. Pass real dimensions into PhotoSwipe's dataSource when known.
 *        Unknown items fall back to a non-extreme 4:5 portrait — close to
 *        most editorial work and benign on the rare misload.
 *     3. Listen to PhotoSwipe's `contentLoadImage` event so that once the
 *        full-resolution image is in memory we patch the slide's true
 *        dimensions in. This guarantees correct framing even if the user
 *        opens an image whose thumb hasn't fully loaded yet.
 *
 * Each `item` shape:
 *   {
 *     id?:          string,    // optional, defaults to publicId or index
 *     publicId?:    string,    // Cloudinary public id (preferred path)
 *     src?:         string,    // direct URL (fallback if publicId not given)
 *     alt:          string,    // required for a11y
 *     caption?:     string,    // optional, shown in lightbox footer
 *     ratio?:       number,    // aspect ratio (w/h) used ONLY for the
 *                              //   masonry thumb's reserved space — not the
 *                              //   lightbox. Defaults to 4:5 portrait.
 *     inquiryType?: string,    // optional per-image override for the
 *                              //   lightbox CTA. Falls back to the
 *                              //   gallery-level `defaultInquiryType` prop.
 *   }
 *
 * Gallery-level props of note:
 *   - defaultInquiryType: string — used by the lightbox CTA when an item
 *     doesn't carry its own `inquiryType`. Tune this per page (e.g. on the
 *     /business page set it to "Brand Story Session" so the lightbox CTA
 *     deep-links visitors to the most relevant inquiry option).
 */

const DEFAULT_RATIO = 0.8;       // 4:5 — used only for thumb space reservation
// Lightbox fallback dims: 2:3 portrait. PhotoSwipe's `preloadFirstSlide`
// can cache the very first slide's dims before our `itemData` listener
// gets a chance to inject natural dimensions. Sizing the fallback to 2:3
// (the dominant ratio for editorial portraits in this gallery) keeps the
// slide box close enough to natural that the `object-fit: contain` rule
// only ever introduces a few pixels of letterboxing — invisible against
// the semi-transparent backdrop.
const FALLBACK_LB_W = 1600;
const FALLBACK_LB_H = 2400;      // 2:3 portrait, matches editorial norm

// Cloudinary transform helpers. `c_limit` downscales when source is larger
// than the requested width but preserves the natural aspect ratio — exactly
// what we need so the lightbox never crops.
const thumbUrl = (publicId, w = 720) =>
    cld(publicId, `f_auto,q_auto,w_${w},c_limit`);

const fullUrl = (publicId, w = 2400) =>
    cld(publicId, `f_auto,q_auto,w_${w},c_limit`);

// LQIP — tiny 32 px wide, heavily blurred, q_auto:low. Cloudinary returns
// this in ~1-2 KB which downloads near-instantly even on a hotel wifi.
const blurUrl = (publicId) =>
    cld(publicId, "w_32,e_blur:800,q_auto:low,f_auto");

const srcSetForThumb = (publicId) => [
    `${thumbUrl(publicId, 480)} 480w`,
    `${thumbUrl(publicId, 720)} 720w`,
    `${thumbUrl(publicId, 960)} 960w`,
].join(", ");

const MediaGalleryItem = ({ item, index, onOpen, onNaturalDims }) => {
    const imgRef = useRef(null);
    const videoRef = useRef(null);
    const [loaded, setLoaded] = useState(false);

    // Phase 14 — branch on item.mediaType. Video items skip the entire
    // <img>/srcSet/blur-up pipeline below and render a <video> element
    // styled identically to fit the masonry tile. The poster image is
    // emitted by mapGalleryItemToFE (cldPosterUrl extracts the frame at
    // offset 0 from the source video and serves it as f_auto,q_auto).
    const isVideo = item.mediaType === 'video';

    const usingPublicId = !!item.publicId;
    const thumb = isVideo
        ? null
        : usingPublicId
            ? thumbUrl(item.publicId)
            : item.src;
    const blur = !isVideo && usingPublicId ? blurUrl(item.publicId) : null;
    const srcSet = !isVideo && usingPublicId ? srcSetForThumb(item.publicId) : undefined;

    // Use intrinsic dims when Sanity gave them to us (the mapper sets
    // naturalWidth/Height on video items). Otherwise fall back to the
    // consumer-provided ratio or MediaGallery's DEFAULT_RATIO.
    const intrinsicRatio = isVideo && item.naturalWidth && item.naturalHeight
        ? item.naturalWidth / item.naturalHeight
        : null;
    const ratio = intrinsicRatio || item.ratio || DEFAULT_RATIO;

    const handleClick = (e) => {
        e.preventDefault();
        onOpen(index, isVideo ? videoRef.current : imgRef.current);
    };

    const handleLoad = (e) => {
        setLoaded(true);
        const img = e.currentTarget;
        if (img.naturalWidth && img.naturalHeight) {
            onNaturalDims(index, img.naturalWidth, img.naturalHeight);
        }
    };

    // Defensive: when the browser serves the thumb from cache, the <img>
    // can be `complete` *before* React attaches the `onLoad` handler — so
    // `handleLoad` never fires and the parent never learns the natural
    // dimensions. Check on mount and lift them up if so. Without this,
    // the first slide opened (whose thumb is most likely to be cached on
    // a soft refresh) would fall back to PhotoSwipe's 4:5 fallback ratio.
    useEffect(() => {
        if (isVideo) {
            // Phase 14 — video items already carry intrinsic dims from the
            // Sanity Cloudinary asset; lift them directly without a probe.
            if (item.naturalWidth && item.naturalHeight) {
                onNaturalDims(index, item.naturalWidth, item.naturalHeight);
            }
            return;
        }
        const img = imgRef.current;
        if (img && img.complete && img.naturalWidth && img.naturalHeight) {
            setLoaded(true);
            onNaturalDims(index, img.naturalWidth, img.naturalHeight);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Phase 14 — IntersectionObserver-driven autoplay throttle. With 8+
    // video tiles on a page, decoding them all simultaneously melts mobile
    // CPU, drains battery, and tanks scroll FPS to teens. This pauses
    // off-screen videos and only autoplays when the tile is ≥40% in view.
    // Also respects prefers-reduced-motion: reduce — those users see the
    // poster image only with a visible Play icon affordance (tap → opens
    // lightbox where the user can manually press play).
    useEffect(() => {
        if (!isVideo) return;
        const vid = videoRef.current;
        if (!vid) return;
        const reducedMotion =
            typeof window !== 'undefined' &&
            window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (reducedMotion) return;

        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // play() returns a promise that rejects if autoplay
                    // is blocked (some mobile browsers, low-power mode,
                    // etc). Swallow the rejection — the poster image is
                    // already visible so the tile never looks broken.
                    vid.play().catch(() => {});
                } else {
                    vid.pause();
                }
            },
            { threshold: 0.4 }
        );
        io.observe(vid);
        return () => io.disconnect();
    }, [isVideo]);

    return (
        <button
            type="button"
            data-testid={`media-gallery-item-${item.id || item.publicId || index}`}
            data-media-type={isVideo ? 'video' : 'image'}
            onClick={handleClick}
            className="group block w-full mb-3 sm:mb-4 overflow-hidden rounded-[2px] bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lux-oxblood))] focus-visible:ring-offset-2"
            aria-label={`Open ${item.alt || (isVideo ? 'video' : 'image')} in lightbox`}
            style={{ breakInside: "avoid", aspectRatio: ratio }}
        >
            <div className="relative w-full h-full overflow-hidden">
                {isVideo ? (
                    <>
                        {/* In-grid video — muted/looped/playsinline, autoplay
                          * gated by the IntersectionObserver above. The
                          * poster fills the tile immediately so visitors
                          * never see a blank/black frame while metadata
                          * loads, even on a 3G connection. */}
                        <video
                            ref={videoRef}
                            src={item.videoSrc}
                            poster={item.posterSrc || undefined}
                            muted
                            loop
                            playsInline
                            preload="metadata"
                            aria-label={item.alt}
                            className={cn(
                                "absolute inset-0 h-full w-full object-cover transition-transform duration-500",
                                "group-hover:scale-[1.02]"
                            )}
                            onLoadedMetadata={(e) => {
                                const v = e.currentTarget;
                                if (v.videoWidth && v.videoHeight) {
                                    onNaturalDims(
                                        index,
                                        v.videoWidth,
                                        v.videoHeight
                                    );
                                }
                            }}
                        />
                        {/* Small play icon — visible at rest, fades on
                          * hover. Confirms the tile is interactive even
                          * before autoplay engages, and gives a clear
                          * affordance for prefers-reduced-motion users. */}
                        <div
                            aria-hidden
                            className="pointer-events-none absolute bottom-3 right-3 flex items-center justify-center h-8 w-8 rounded-full bg-black/55 backdrop-blur-[2px] text-white/90 transition-opacity duration-300 group-hover:opacity-0"
                        >
                            <Play className="h-3.5 w-3.5 fill-current" strokeWidth={0} />
                        </div>
                    </>
                ) : (
                    <>
                        {blur && (
                            <img
                                src={blur}
                                alt=""
                                aria-hidden
                                className={cn(
                                    "absolute inset-0 h-full w-full object-cover transition-opacity duration-500",
                                    loaded ? "opacity-0" : "opacity-100",
                                )}
                                style={{ filter: "blur(12px)", transform: "scale(1.05)" }}
                            />
                        )}
                        <img
                            ref={imgRef}
                            src={thumb}
                            srcSet={srcSet}
                            sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                            alt={item.alt}
                            loading="lazy"
                            decoding="async"
                            onLoad={handleLoad}
                            className={cn(
                                "absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-500",
                                loaded ? "opacity-100" : "opacity-0",
                                "group-hover:scale-[1.02]",
                            )}
                        />
                    </>
                )}
            </div>
        </button>
    );
};

export const MediaGallery = ({
    items,
    initialCount = 8,
    incrementCount = 8,
    desktopColumns = 3,
    mobileColumns = 2,
    className,
    galleryId = "media-gallery",
    defaultInquiryType = "",
}) => {
    const navigate = useNavigate();
    const [visibleCount, setVisibleCount] = useState(initialCount);
    const visibleItems = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);
    const hasMore = visibleCount < items.length;
    const lightboxRef = useRef(null);
    const containerRef = useRef(null);

    // Per-index natural dimensions captured from loaded thumbs.
    const naturalDimsRef = useRef({});

    const onNaturalDims = useCallback((index, w, h) => {
        naturalDimsRef.current[index] = { w, h };
    }, []);

    // Pre-warm the natural dimensions of the first image. The first slide
    // is the most-clicked entry point and the most likely to suffer from
    // the cache-race where the thumb's `onLoad` event fires before React
    // attaches its handler — leaving naturalDimsRef[0] empty at open time
    // and forcing PhotoSwipe to use the 4:5 fallback ratio.
    //
    // This probe fires a no-cost `new Image()` that resolves the natural
    // dimensions in the background. The browser deduplicates with the
    // actual thumb fetch, so this is essentially free. By click time the
    // first slide always has correct dims regardless of load timing.
    useEffect(() => {
        if (!items.length) return;
        const first = items[0];
        if (naturalDimsRef.current[0]) return;
        const url = first.publicId ? thumbUrl(first.publicId) : first.src;
        if (!url) return;
        const probe = new Image();
        probe.onload = () => {
            if (probe.naturalWidth && probe.naturalHeight) {
                naturalDimsRef.current[0] = {
                    w: probe.naturalWidth,
                    h: probe.naturalHeight,
                };
            }
        };
        probe.src = url;
    }, [items]);

    // Build PhotoSwipe dataSource. Uses a NORMALIZED natural size for
    // every slide: aspect ratio preserved from the loaded thumb (or the
    // 2:3 fallback if unknown), but width fixed at the full-resolution
    // Cloudinary transform width (2400 px — matches `fullUrl()`'s
    // `w_2400` transform).
    //
    // Why normalize: PhotoSwipe v5's default `fit` zoom level caps at 1×
    // (no upscaling above the slide's reported natural width/height).
    // When we passed the thumb's tiny dimensions (e.g. 480×720 for a
    // mobile-srcset variant), PhotoSwipe rendered the lightbox image at
    // that exact tiny size — barely larger than the thumbnail. Reporting
    // a deliberately large natural size (2400 wide) lets the "fit" zoom
    // scale DOWN to fill the viewport for every slide, regardless of
    // which srcset variant the browser happened to load for the thumb.
    const buildDataSource = useCallback(
        () =>
            items.map((item, idx) => {
                const known = naturalDimsRef.current[idx];
                // Aspect from the loaded thumb (preserves natural ratio
                // since Cloudinary `c_limit` doesn't crop). Falls back to
                // 2:3 portrait — the editorial norm in this gallery.
                const aspect = known && known.h
                    ? known.w / known.h
                    : FALLBACK_LB_W / FALLBACK_LB_H;
                const width = 2400;
                const height = Math.round(width / aspect);

                // Phase 14 — video items use PhotoSwipe v5's `type: 'html'`
                // slide so we can render a controlled <video> element with
                // full playback controls in the lightbox. The HTML is
                // injected once on slide content load; the change/close
                // hooks below pause the video element on navigation.
                //
                // We keep the same width/height/caption/inquiryType shape
                // so the caption strip + "Request a shoot like this" CTA
                // continue to work uniformly for video and image slides.
                if (item.mediaType === 'video') {
                    const vidSrc = item.videoSrc || '';
                    const posterSrc = item.posterSrc || '';
                    // Escape for HTML attribute context — Cloudinary URLs
                    // contain only safe chars, but belt-and-braces.
                    const escAttr = (s) =>
                        String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
                    return {
                        type: 'html',
                        html: `
                            <div class="pswp__video-wrap" style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;padding:0;">
                                <video
                                    class="pswp__video-el"
                                    controls
                                    autoplay
                                    playsinline
                                    preload="metadata"
                                    poster="${escAttr(posterSrc)}"
                                    src="${escAttr(vidSrc)}"
                                    style="max-width:100%;max-height:100%;background:#000;outline:none;display:block;"
                                ></video>
                            </div>
                        `,
                        width,
                        height,
                        alt: item.alt,
                        caption: item.caption || item.alt,
                        inquiryType: item.inquiryType || defaultInquiryType || "",
                    };
                }

                return {
                    src: item.publicId ? fullUrl(item.publicId) : item.src,
                    width,
                    height,
                    alt: item.alt,
                    caption: item.caption || item.alt,
                    inquiryType: item.inquiryType || defaultInquiryType || "",
                };
            }),
        [items, defaultInquiryType],
    );

    useEffect(() => {
        if (!containerRef.current) return;
        const lightbox = new PhotoSwipeLightbox({
            dataSource: buildDataSource(),
            pswpModule: PhotoSwipe,
            // Round 5 refinement: backdrop opacity 0.85 → 0.93 to push
            // the surrounding page nearly invisible behind the open photo.
            // The `.pswp__bg` CSS in index.css remains the authoritative
            // value (with !important and the 10 px backdrop-filter blur).
            bgOpacity: 0.93,
            showHideAnimationType: "fade",
            zoom: true,
            counter: true,
            // Reserve only enough bottom strip for the "Request a shoot
            // like this" CTA + caption. Earlier padding (140px) left the
            // photo at ~78% of viewport height; tightening to 96px brings
            // portrait images into the 85–90% target band per the brief.
            paddingFn: (viewportSize) => {
                const isSmall = viewportSize.x < 640;
                return {
                    top: isSmall ? 40 : 48,
                    bottom: isSmall ? 100 : 96,
                    left: isSmall ? 12 : 24,
                    right: isSmall ? 12 : 24,
                };
            },
            // Mobile gestures — PhotoSwipe v5 includes pinch-zoom,
            // double-tap-to-zoom, momentum-swipe-to-next, and
            // swipe-down-to-close out of the box.
        });

        // Inject per-slide aspect-preserving dimensions on every itemData
        // lookup (the only reliable hook in PhotoSwipe v5). We normalize
        // to a fixed 2400-wide so the "fit" zoom level always has enough
        // natural size to scale DOWN to fill the viewport — see the
        // explanation on `buildDataSource` above for the deeper rationale.
        lightbox.on("itemData", (e) => {
            const known = naturalDimsRef.current[e.index];
            if (known && known.w && known.h) {
                const aspect = known.w / known.h;
                e.itemData.width = 2400;
                e.itemData.height = Math.round(2400 / aspect);
            }
        });

        // Register lightbox UI elements: caption strip + "Request a shoot
        // like this" CTA. PhotoSwipe's `uiRegister` hook fires once per open.
        lightbox.on("uiRegister", () => {
            // Caption — small text strip just under the photo.
            lightbox.pswp.ui.registerElement({
                name: "caption",
                order: 9,
                isButton: false,
                appendTo: "root",
                html: "",
                onInit: (el, pswp) => {
                    el.className = "pswp__caption";
                    pswp.on("change", () => {
                        const slide = pswp.currSlide?.data;
                        el.textContent = slide?.caption || "";
                        el.style.display = slide?.caption ? "block" : "none";
                    });
                },
            });

            // CTA — "Request a shoot like this" button anchored bottom-center.
            // Reads the current slide's `inquiryType` and navigates to
            // /inquire?type=<encoded>. Closes the lightbox first so the inquire
            // route mounts cleanly (otherwise PhotoSwipe's body-scroll-lock
            // would persist across navigations).
            lightbox.pswp.ui.registerElement({
                name: "inquire-cta",
                order: 10,
                isButton: false,
                appendTo: "root",
                html: "Request a shoot like this",
                onInit: (el, pswp) => {
                    el.className = "pswp__inquire-cta";
                    el.setAttribute("role", "button");
                    el.setAttribute("tabindex", "0");
                    el.setAttribute("data-testid", "pswp-inquire-cta");

                    const navigateToInquire = () => {
                        const slide = pswp.currSlide?.data;
                        const type = slide?.inquiryType || "";
                        const href = type
                            ? `/inquire?type=${encodeURIComponent(type)}`
                            : "/inquire";
                        // Close first, then SPA-navigate on the next tick.
                        pswp.close();
                        setTimeout(() => navigate(href), 60);
                    };

                    el.addEventListener("click", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigateToInquire();
                    });
                    el.addEventListener("keydown", (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigateToInquire();
                        }
                    });
                },
            });
        });

        lightboxRef.current = lightbox;
        lightbox.init();

        // Toggle a body class while the lightbox is open so other UI
        // (notably the mobile-only sticky inquiry CTA in Layout.jsx) can
        // suppress itself via CSS. Cleaner than wiring a React context
        // through the whole tree just to communicate one boolean.
        //
        // `beforeOpen` fires on the Lightbox plugin instance the moment a
        // user click triggers `loadAndOpen`. `destroy` fires after the
        // close animation completes (PhotoSwipe v5 destroys the core
        // instance on every close in lightbox mode, then re-creates it
        // on the next open) — so this pair brackets the entire open state.
        const onLightboxOpen = () => document.body.classList.add("pswp-open");
        const onLightboxClose = () => document.body.classList.remove("pswp-open");
        lightbox.on("beforeOpen", onLightboxOpen);
        lightbox.on("destroy", onLightboxClose);

        // Phase 14 — pause any playing <video> elements when the user
        // navigates to a new slide or closes the lightbox. Without this
        // hook, leaving an audio-track video playing while swiping to the
        // next slide produces ghost audio with no visible source. Querying
        // the live DOM (rather than ref-tracking each video) keeps the
        // logic resilient to PhotoSwipe's slide-recycling behavior.
        const pauseAllLightboxVideos = () => {
            const videos = document.querySelectorAll('.pswp__container video, .pswp__video-el');
            videos.forEach((v) => {
                try { v.pause(); } catch (_) { /* element may already be detached */ }
            });
        };
        lightbox.on("change", pauseAllLightboxVideos);
        lightbox.on("close", pauseAllLightboxVideos);

        return () => {
            // Belt-and-braces: ensure the body class never sticks if the
            // gallery component unmounts mid-open (e.g. user navigates
            // away via the inquire CTA before the close animation runs).
            document.body.classList.remove("pswp-open");
            lightbox.destroy();
            lightboxRef.current = null;
        };
    }, [buildDataSource, navigate]);

    const open = useCallback(
        (index, thumbEl) => {
            if (!lightboxRef.current) return;
            if (thumbEl?.naturalWidth && thumbEl?.naturalHeight) {
                naturalDimsRef.current[index] = {
                    w: thumbEl.naturalWidth,
                    h: thumbEl.naturalHeight,
                };
            }
            // Pass a freshly built dataSource as a belt-and-braces measure;
            // the `itemData` listener above is what actually does the
            // per-slide dim injection, but this keeps options.dataSource
            // in sync for any internal PhotoSwipe code paths that read it
            // directly (e.g. preloading the next slide).
            lightboxRef.current.loadAndOpen(index, buildDataSource());
        },
        [buildDataSource],
    );

    // Phase 8 — switch from CSS Columns to JS-driven left-to-right
    // distribution. CSS Columns filled column-first (item 1 above item 2,
    // same column), which broke natural reading order. The Masonry
    // component distributes by `index % numColumns` so items 1/2/3 land
    // across the top row, items 4/5/6 across the second row, etc.
    //
    // Breakpoint map mirrors the legacy "columns-2 md:columns-N" behavior:
    // `mobileColumns` cols on mobile/tablet (<768px), caller-driven
    // `desktopColumns` on md and up. The mobileColumns override lets
    // sections like Motion (single large video) request a 1-col mobile
    // layout instead of the masonry default of 2. Per-item rendering is
    // identical (each card retains its `style.aspectRatio` for space
    // reservation); only the wrapping element + flow direction changes.
    // PhotoSwipe lightbox wiring is untouched.
    return (
        <div ref={containerRef} className={cn("w-full", className)} data-testid={galleryId}>
            <Masonry
                items={visibleItems}
                breakpoints={{base: mobileColumns, md: desktopColumns}}
                gap="gap-3 sm:gap-4"
                testId={`${galleryId}-masonry`}
                renderItem={(item, idx) => (
                    <MediaGalleryItem
                        key={item.id || item.publicId || idx}
                        item={item}
                        index={idx}
                        onOpen={open}
                        onNaturalDims={onNaturalDims}
                    />
                )}
            />
            {hasMore && (
                <div className="mt-6 flex justify-center">
                    <button
                        type="button"
                        data-testid={`${galleryId}-load-more`}
                        onClick={() => setVisibleCount((c) => c + incrementCount)}
                        className="btn-request btn-request--sm btn-request--ghost"
                        style={{ minHeight: "44px" }}
                    >
                        Load More ({items.length - visibleCount} remaining)
                    </button>
                </div>
            )}
        </div>
    );
};

export default MediaGallery;
